import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, UserIcon, CheckIcon, XMarkIcon as XIcon } from '@heroicons/react/24/outline';

const UserDetailsModal = ({ isOpen, onClose, user, onEdit }) => {
  const { t } = useTranslation();

  if (!isOpen || !user) return null;

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      operations: 'bg-blue-100 text-blue-800',
      accounting: 'bg-green-100 text-green-800',
      client: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadgeColor = (status) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.not_available');
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-gray-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('users.basic_information')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.first_name')}
                </label>
                <p className="mt-1 text-sm text-gray-900">{user.firstName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.last_name')}
                </label>
                <p className="mt-1 text-sm text-gray-900">{user.lastName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.email')}
                </label>
                <p className="mt-1 text-sm text-gray-900">{user.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.user_id')}
                </label>
                <p className="mt-1 text-sm text-gray-900 font-mono">{user.id}</p>
              </div>
            </div>
          </div>

          {/* Role and Status */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('users.role_and_status')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.role')}
                </label>
                <div className="mt-1">
                  <span className={`badge ${getRoleBadgeColor(user.role)}`}>
                    {t(`users.${user.role}`)}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.status')}
                </label>
                <div className="mt-1">
                  <span className={`badge ${getStatusBadgeColor(user.status)}`}>
                    {t(`users.${user.status}`)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('users.permissions')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {user.permissions && Object.entries(user.permissions).map(([permission, enabled]) => (
                <div key={permission} className="flex items-center space-x-2">
                  {enabled ? (
                    <CheckIcon className="w-4 h-4 text-green-500" />
                  ) : (
                    <XIcon className="w-4 h-4 text-red-500" />
                  )}
                  <span className={`text-sm ${enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                    {t(`navigation.${permission}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Activity Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('users.activity_information')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.last_login')}
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatDate(user.lastLogin)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.created_at')}
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatDate(user.createdAt)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.updated_at')}
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatDate(user.updatedAt)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  {t('users.login_count')}
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {user.loginCount || 0} {t('users.times')}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          {user.notes && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('users.notes')}
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700">{user.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 border-t">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            {t('common.close')}
          </button>
          {onEdit && (
            <button
              onClick={() => {
                onEdit(user);
                onClose();
              }}
              className="btn btn-primary"
            >
              {t('common.edit')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;